import React, { useState, useEffect } from 'react';
import { X, Upload, Plus, Volume2 } from 'lucide-react';
import { soundEffectAPI, categoryAPI } from '../../services/api';
import CategoryCreateDialog from './CategoryCreateDialog';

const SoundEffectUploadDialog = ({ isOpen, onClose, onUploadComplete }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, currentFile: '' });
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [loadingSubCategories, setLoadingSubCategories] = useState(false);
  const [showCategoryCreateDialog, setShowCategoryCreateDialog] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    category: '',
    subcategory: '',
    tags: '',
    files: []
  });

  useEffect(() => {
    if (isOpen) {
      fetchCategories();
      // Reset form when dialog opens
      setUploadForm({
        category: '',
        subcategory: '',
        tags: '',
        files: []
      });
      setUploadProgress({ current: 0, total: 0, currentFile: '' });
    }
  }, [isOpen]);

  // Fetch subcategories when upload form category changes
  useEffect(() => {
    if (uploadForm.category && uploadForm.category !== '') {
      fetchSubCategories(uploadForm.category);
    } else {
      setSubCategories([]);
    }
  }, [uploadForm.category]);

  const fetchCategories = async () => {
    try {
      const response = await categoryAPI.getAll({
        type: 'sound-effect',
        parentId: '', // Only root categories
        isActive: true
      });

      if (response.data.success) {
        const categoriesData = response.data.data || [];
        // Sort categories by name
        categoriesData.sort((a, b) => {
          const nameA = String(a.name || '').toLowerCase();
          const nameB = String(b.name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        setCategories(categoriesData);
      } else {
        setCategories([]);
      }
    } catch (error) {
      console.error('Error fetching sound effect categories:', error);
      setCategories([]);
    }
  };

  const fetchSubCategories = async (category_id) => {
    try {
      setLoadingSubCategories(true);
      const response = await categoryAPI.getAll({
        parentId: category_id,
        type: 'sound-effect',
        isActive: true
      });

      if (response.data.success) {
        const subCategoriesData = response.data.data || [];
        // Sort subcategories by name
        subCategoriesData.sort((a, b) => {
          const nameA = String(a.name || '').toLowerCase();
          const nameB = String(b.name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        setSubCategories(subCategoriesData);
      } else {
        setSubCategories([]);
      }
    } catch (error) {
      console.error('Error fetching sound effect subcategories:', error);
      setSubCategories([]);
    } finally {
      setLoadingSubCategories(false);
    }
  };

  const handleCategoryCreated = (newCategory) => {
    // Refresh categories list
    fetchCategories();
    // Auto-select the newly created category
    setUploadForm(prev => ({ ...prev, category: newCategory.id, subcategory: '' }));
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length === 0) return;

    const allowedTypes = ['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/mp4', 'audio/aac'];
    const maxSize = 50 * 1024 * 1024; // 50MB
    const validFiles = [];
    const errors = [];

    selectedFiles.forEach((file, index) => {
      // Validate file type
      if (!allowedTypes.includes(file.type)) {
        errors.push(`${file.name}: Invalid file type (must be MP3, WAV, OGG, MP4, or AAC)`);
        return;
      }

      // Validate file size
      if (file.size > maxSize) {
        errors.push(`${file.name}: File size exceeds 50MB limit`);
        return;
      }

      // Extract name from filename (remove extension)
      const nameFromFile = file.name.replace(/\.[^/.]+$/, '');

      validFiles.push({
        file,
        name: nameFromFile,
        description: nameFromFile, // Use filename as description too
        originalName: file.name
      });
    });

    // Show errors if any
    if (errors.length > 0) {
      alert(`Some files were skipped:\n\n${errors.join('\n')}`);
    }

    // Update form with valid files
    if (validFiles.length > 0) {
      setUploadForm(prev => ({
        ...prev,
        files: validFiles
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!uploadForm.files || uploadForm.files.length === 0) {
      alert('Please select sound effect files');
      return;
    }

    if (!uploadForm.category) {
      alert('Please select a category');
      return;
    }

    try {
      setUploading(true);
      const totalFiles = uploadForm.files.length;
      let successCount = 0;
      let failedFiles = [];

      // Initialize progress
      setUploadProgress({ current: 0, total: totalFiles, currentFile: '' });

      // Upload files one by one
      for (let i = 0; i < uploadForm.files.length; i++) {
        const fileData = uploadForm.files[i];

        // Update progress
        setUploadProgress({ current: i + 1, total: totalFiles, currentFile: fileData.name });

        try {
          const formData = new FormData();
          formData.append('file', fileData.file);
          formData.append('name', fileData.name.trim());
          formData.append('description', fileData.description.trim());
          formData.append('category_id', uploadForm.subcategory || uploadForm.category);
          formData.append('tags', uploadForm.tags.trim());

          const response = await soundEffectAPI.upload(formData);

          if (response.data.success) {
            successCount++;
          } else {
            failedFiles.push(`${fileData.name}: ${response.data.message || 'Upload failed'}`);
          }
        } catch (error) {
          console.error(`Error uploading ${fileData.name}:`, error);
          failedFiles.push(`${fileData.name}: ${error.response?.data?.message || error.message}`);
        }
      }

      // Show results
      let message = `Upload completed!\n\n✅ Successfully uploaded: ${successCount}/${totalFiles} files`;

      if (failedFiles.length > 0) {
        message += `\n\n❌ Failed uploads:\n${failedFiles.join('\n')}`;
      }

      alert(message);

      // Close dialog and refresh if any files were successful
      if (successCount > 0) {
        onClose();
        if (onUploadComplete) {
          onUploadComplete();
        }
      }
    } catch (error) {
      console.error('Error during batch upload:', error);
      alert(`Batch upload failed: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <Volume2 className="w-6 h-6 text-purple-600" />
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Upload Sound Effects</h2>
                <p className="text-sm text-gray-500">Upload multiple sound effect files at once</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={uploading}
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* File Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sound Effect Files *
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-purple-400 transition-colors">
                <input
                  type="file"
                  accept="audio/*"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                  id="soundeffect-file-input"
                  disabled={uploading}
                />
                <label
                  htmlFor="soundeffect-file-input"
                  className="cursor-pointer flex flex-col items-center space-y-2"
                >
                  <Upload className="w-8 h-8 text-gray-400" />
                  <span className="text-sm text-gray-600">
                    {uploadForm.files.length > 0
                      ? `${uploadForm.files.length} file${uploadForm.files.length !== 1 ? 's' : ''} selected`
                      : 'Click to select sound effect files'
                    }
                  </span>
                  <span className="text-xs text-gray-500">
                    MP3, WAV, OGG, MP4, AAC (max 50MB each) • Multiple files supported
                  </span>
                </label>
              </div>

              {/* Selected Files List */}
              {uploadForm.files.length > 0 && (
                <div className="mt-3 max-h-32 overflow-y-auto">
                  <div className="text-xs font-medium text-gray-700 mb-2">Selected Files:</div>
                  <div className="space-y-1">
                    {uploadForm.files.map((fileData, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 px-2 py-1 rounded text-xs">
                        <div className="flex items-center space-x-2 flex-1 min-w-0">
                          <Volume2 className="w-3 h-3 text-purple-500 flex-shrink-0" />
                          <span className="truncate font-medium">{fileData.name}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setUploadForm(prev => ({
                              ...prev,
                              files: prev.files.filter((_, i) => i !== index)
                            }));
                          }}
                          className="text-gray-400 hover:text-red-500 ml-2 flex-shrink-0"
                          disabled={uploading}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Info Note */}
            {uploadForm.files.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-start space-x-2">
                  <div className="text-blue-600 mt-0.5">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="text-sm text-blue-800">
                    <p className="font-medium">Automatic naming</p>
                    <p>Names and descriptions will be automatically extracted from filenames. You can edit them later if needed.</p>
                  </div>
                </div>
              </div>
            )}

            {/* Category Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Category *
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowCategoryCreateDialog(true)}
                    className="text-xs text-purple-600 hover:text-purple-700 flex items-center space-x-1"
                    disabled={uploading}
                  >
                    <Plus className="w-3 h-3" />
                    <span>Add missing category</span>
                  </button>
                </div>
                <select
                  value={uploadForm.category}
                  onChange={(e) => {
                    setUploadForm(prev => ({ ...prev, category: e.target.value, subcategory: '' }));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  disabled={uploading}
                  required
                >
                  <option value="">Select category</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subcategory
                </label>
                <select
                  value={uploadForm.subcategory}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, subcategory: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  disabled={uploading || loadingSubCategories || subCategories.length === 0}
                >
                  <option value="">Select subcategory</option>
                  {subCategories.map((subcategory) => (
                    <option key={subcategory.id} value={subcategory.id}>
                      {subcategory.name}
                    </option>
                  ))}
                </select>
                {loadingSubCategories && (
                  <p className="text-xs text-gray-500 mt-1">Loading subcategories...</p>
                )}
              </div>
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tags
              </label>
              <input
                type="text"
                value={uploadForm.tags}
                onChange={(e) => setUploadForm(prev => ({ ...prev, tags: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Enter tags separated by commas"
                disabled={uploading}
              />
              <p className="text-xs text-gray-500 mt-1">
                Add tags to help others find your sound effects (e.g., explosion, nature, ambient). These tags will be applied to all uploaded files.
              </p>
            </div>

            {/* Upload Progress */}
            {uploading && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600"></div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-purple-900">
                      Uploading {uploadProgress.current} of {uploadProgress.total} files...
                    </div>
                    {uploadProgress.currentFile && (
                      <div className="text-xs text-purple-700 mt-1">
                        Current: {uploadProgress.currentFile}
                      </div>
                    )}
                  </div>
                </div>
                <div className="w-full bg-purple-200 rounded-full h-2">
                  <div
                    className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                  ></div>
                </div>
                <div className="text-xs text-purple-600 mt-2 text-center">
                  {Math.round((uploadProgress.current / uploadProgress.total) * 100)}% complete
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                disabled={uploading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={uploading || !uploadForm.files.length || !uploadForm.category}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
              >
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Uploading {uploadForm.files.length} file{uploadForm.files.length !== 1 ? 's' : ''}...</span>
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    <span>
                      Upload {uploadForm.files.length > 0 ? `${uploadForm.files.length} ` : ''}Sound Effect{uploadForm.files.length !== 1 ? 's' : ''}
                    </span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Category Create Dialog */}
      <CategoryCreateDialog
        isOpen={showCategoryCreateDialog}
        onClose={() => setShowCategoryCreateDialog(false)}
        onCategoryCreated={handleCategoryCreated}
        categoryType="sound-effect"
      />
    </>
  );
};

export default SoundEffectUploadDialog;
